import {
  IPublicClientApplication,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

export const getBackendUri = (): string => {
  return import.meta.env.VITE_BACKEND_URI || `${window.location.origin}/`;
};
export const BACKEND_URI = getBackendUri().replace(/\/$/, ""); // Ensure no trailing slash
export interface AuthConfig {
  tenantId: string;
  clientId: string;
  apiScope: string;
}
export const fetchAuthConfig = async (): Promise<AuthConfig> => {
  if (authConfig) return authConfig;

  const response = await fetch(`${BACKEND_URI}/api/config/auth`);

  if (!response.ok) {
    throw new Error(`Failed to fetch auth config: ${response.status}`);
  }

  authConfig = await response.json();
  return authConfig as AuthConfig;
};
// Initialize MSAL instance with config

export const initMsal = async (): Promise<IPublicClientApplication> => {
  if (msalInstance) return msalInstance;

  const config = await fetchAuthConfig();

  const msalConfig = {
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "sessionStorage" as const,
      storeAuthStateInCookie: false,
    },
  };

  msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  return msalInstance as PublicClientApplication;
};
export const getTokenRequest = async () => {
  const config = await fetchAuthConfig();
  return {
    scopes: [config.apiScope],
  };
};
export const getRedirectUri = () => window.location.origin;
export const getUserGroups = async (
  client: IPublicClientApplication,
): Promise<string[]> => {
  try {
    const account = client.getActiveAccount();
    return (account?.idTokenClaims?.groups as string[]) || [];
  } catch (error) {
    console.error("Failed to get user groups:", error);
    return [];
  }
};
// One interactive sign-in at a time: parallel requests that need it share the same popup.
let interactiveTokenRequest: Promise<string | undefined> | null = null;

/**
 * Access token for the backend API.
 *
 * `acquireTokenSilent` serves the token from MSAL's cache and only contacts Entra ID when it is
 * about to expire. A popup is opened only when Entra ID requires user interaction (for example a
 * new sign-in after the conditional-access sign-in frequency), not for network or other errors.
 */
export const getToken = async (
  client: IPublicClientApplication,
): Promise<string | undefined> => {
  const account = client.getActiveAccount();
  if (!account) return undefined;
  const tokenRequest = await getTokenRequest();

  try {
    const response = await client.acquireTokenSilent({
      ...tokenRequest,
      account,
      redirectUri: getRedirectUri(),
    });
    return response.accessToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      console.error("Failed to acquire token silently:", error);
      return undefined;
    }
    if (!interactiveTokenRequest) {
      interactiveTokenRequest = client
        .acquireTokenPopup({ ...tokenRequest, redirectUri: getRedirectUri() })
        .then((response) => response.accessToken)
        .catch((popupError) => {
          console.error("Failed to acquire token via popup:", popupError);
          return undefined;
        })
        .finally(() => {
          interactiveTokenRequest = null;
        });
    }
    return interactiveTokenRequest;
  }
};
export const loginRequest = {
  scopes: ["openid", "profile", "email"],
};
export const requireAccessControl = true;
export const useLogin = true;
export let msalInstance: IPublicClientApplication | null = null;
export let authConfig: AuthConfig | null = null;
